import DesignTokens from '@tableau/design-tokens';
import { IShareContentRecipient } from '@tableau/share-dialog';
import { ButtonType, ButtonWidget, DialogPositioningBehavior, DialogType, TabStyles, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { ModalInstance, ReactModalOptions, ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { IToastOptions, ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Set } from '~commonInfra/utils/Set';
import { setExplicitPermissionsForGrantees } from '~permissions/requests/ExplicitPermissions';
import { CapabilityValue, ICapabilityValue } from '~permissions/requests/types/ICapabilityValue';
import { EditPermissionsAction } from '~permissions/services/EditPermissionsAction';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Formatters, Messages } from '~src/codegen/Localize';
import { setCollectionVisibility } from '~src/Collections/actions/SetCollectionVisibilityAction';
import { CollectionVisibility } from '~src/Collections/space/CollectionModels';
import { CollectionsDialogStyles } from '~src/Collections/space/CollectionsDialogStyles';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { interpolateString } from '~src/commonInfra/utils/StringInterpolation';
import { ITypedId, ITypedIdWithName } from '~src/httpClient/serverApi/types/ITypedId';
import { rejectIfResultContainsError } from '~src/httpClient/utils/HttpErrorChecker';
import { AuthorizableType } from '~src/permissions/types/AuthorizableType';
import { AI_ACCESS_CAPABILITY_SCOPE } from '~src/permissions/types/CapabilityScope';
import { CapabilitySectionsByAuthorizableType } from '~src/permissions/types/CapabilitySections';
import { PermissionRoleName } from '~src/permissions/types/PermissionRole';
import { PermissionRoleHelper } from '~src/permissions/utils/PermissionRoleHelper';
import { PermissionsHelper } from '~src/permissions/utils/PermissionsHelper';
import { sendGrantAccessDialogClickedTelemetry, sendManagePermissionsClickedTelemetry } from '~src/requestAccess/telemetry/RequestAccessTelemetry';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { ExplorableItemType, ExplorableItemTypeValue } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { applyColor }from '~src/style/ColorService';


export namespace GrantPermissionsAfterSharingDialog {
  const DIALOG_WIDTH = 500;
  export const DIALOG_TESTID = 'collection-grant-access';
  export const MANAGE_PERMISSIONS_BUTTON_TESTID = DIALOG_TESTID + '-manage-permissions';
  export const DISMISS_BUTTON_TESTID = DIALOG_TESTID + '-dismiss';
  export const GRANT_ACCESS_BUTTON_TESTID = DIALOG_TESTID + '-grant-access';
  export const MESSAGE_DETAIL_TESTID = DIALOG_TESTID + '-message-detail';
  export const LEARN_MORE_LINK_TESTID = DIALOG_TESTID + '-learn-more';

  export interface ContentsProps {
    content: IContentInfo<ExplorableItemType>;
    shareRecipients: IShareContentRecipient[];
    unpermissionedRecipients: ITypedId[];
    sourceAuthorizable?: ITypedIdWithName;
    isRequestAccessDialogEnabled?: boolean;
    requesterName?: string;
    requesterUsername?: string;
    // When true, the dialog grants the `ai_access` capability (with AI-specific copy) instead of
    // the View role. Only set for the aiAccess deep-link variant on workbook, view, and project content.
    isAiAccessGrant?: boolean;
  }

  export const enum DialogResult {
    Dismiss = 'dismiss',
    GrantPermissions = 'grant',
    ManagePermissions = 'managePermissions'
  }

  interface MessagesForGrantPermissionsAfterSharing {
    shareOneUser: (a: { CONTENT_NAME: string }) => string;
    shareMultipleUsers: (a: { CONTENT_NAME: string, TOTAL_USER_COUNT: number, TOTAL_USER_COUNT_LOC: string, UNPERMISSIONED_USER_COUNT: number, UNPERMISSIONED_USER_COUNT_LOC: string }) => string;
    shareOneUserLocked?: (a: { CONTENT_NAME: string }) => string;
    shareOneUserLockedWorkbook?: (a: { CONTENT_NAME: string }) => string;
    shareMultipleUsersLocked?: (a: { CONTENT_NAME: string, TOTAL_USER_COUNT: number, TOTAL_USER_COUNT_LOC: string, UNPERMISSIONED_USER_COUNT: number, UNPERMISSIONED_USER_COUNT_LOC: string }) => string;
    shareMultipleUsersLockedWorkbook?: (a: { CONTENT_NAME: string, TOTAL_USER_COUNT: number, TOTAL_USER_COUNT_LOC: string, UNPERMISSIONED_USER_COUNT: number, UNPERMISSIONED_USER_COUNT_LOC: string }) => string;
    permissionsTemplate?: () => string;
    permissionsActionOneUser?: () => string;
    permissionsActionMultipleUsers?: () => string;
    dialogHeader?: (a: { CONTENT_NAME: string }) => string;
    dialogHeaderLocked?: (a: { PROJECT_NAME: string }) => string;
    dialogHeaderLockedWorkbook?: (a: { WORKBOOK_NAME: string }) => string;
    dialogTitle: () => string;
    grantAccessMessage: (a: { USER_FRIENDLY_NAME: string, USERNAME: string, CONTENT_NAME: string }) => string;
    // AI Access variant copy — only populated for content types that expose `ai_access` (workbook/view).
    // These mirror the read-grant `dialogHeader*`/`permissionsTemplate` keys so the whole dialog body
    // (title, header, request message, and template description) reads as AI Access rather than View.
    aiDialogTitle?: () => string;
    grantAiAccessMessage?: (a: { USER_FRIENDLY_NAME: string, USERNAME: string, CONTENT_NAME: string }) => string;
    aiDialogHeader?: (a: { CONTENT_NAME: string }) => string;
    aiDialogHeaderLocked?: (a: { PROJECT_NAME: string }) => string;
    aiDialogHeaderLockedWorkbook?: (a: { WORKBOOK_NAME: string }) => string;
    aiPermissionsTemplate?: () => string;
    // "|"-separated detail copy rendered as a bullet list between the AI request message and the AI
    // template description. AI-only, like the other ai* keys above.
    grantAiAccessMessageDetail?: () => string;
  }

  const ContentTypeToMessagesMap = new Map<ExplorableItemType, MessagesForGrantPermissionsAfterSharing>()
    .set(ExplorableItemTypeValue.collection, {
      shareOneUser: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_collection,
      shareMultipleUsers: Messages.shareGrantPermissionsDialogMessage_collection,
      dialogTitle: Messages.shareGrantPermissionsDialogTitle_collection,
      grantAccessMessage: Messages.grantAccessDialogMessage,
    })
    .set(ExplorableItemTypeValue.datasource, {
      shareOneUser: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_datasource,
      shareMultipleUsers: Messages.shareGrantPermissionsDialogMessage_datasource,
      shareOneUserLocked: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_lockedProject_datasource,
      shareMultipleUsersLocked: Messages.shareGrantPermissionsDialogMessage_lockedProject_datasource,
      dialogTitle: Messages.shareGrantPermissionsDialogTitle_datasource,
      dialogHeader: Messages.shareGrantPermissionDialogHeader_datasource,
      dialogHeaderLocked: Messages.shareGrantPermissionDialogHeader_lockedProject_datasource,
      permissionsTemplate: Messages.shareGrantPermissionsDialogTemplateDescription_datasource,
      grantAccessMessage: Messages.grantAccessDialogMessage,
    })
    .set(ExplorableItemTypeValue.flow, {
      shareOneUser: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_flow,
      shareMultipleUsers: Messages.shareGrantPermissionsDialogMessage_flow,
      shareOneUserLocked: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_lockedProject_flow,
      shareMultipleUsersLocked: Messages.shareGrantPermissionsDialogMessage_lockedProject_flow,
      dialogTitle: Messages.shareGrantPermissionsDialogTitle_flow,
      dialogHeader: Messages.shareGrantPermissionDialogHeader_flow,
      dialogHeaderLocked: Messages.shareGrantPermissionDialogHeader_lockedProject_flow,
      permissionsTemplate: Messages.shareGrantPermissionsDialogTemplateDescription_flow,
      grantAccessMessage: Messages.grantAccessDialogMessage,
    })
    .set(ExplorableItemTypeValue.virtualConnection, {
      shareOneUser: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_virtualConnection,
      shareMultipleUsers: Messages.shareGrantPermissionsDialogMessage_virtualConnection,
      shareOneUserLocked: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_lockedProject_virtualConnection,
      shareMultipleUsersLocked: Messages.shareGrantPermissionsDialogMessage_lockedProject_virtualConnection,
      dialogTitle: Messages.shareGrantPermissionsDialogTitle_virtualConnection,
      dialogHeader: Messages.shareGrantPermissionDialogHeader_virtualConnection,
      dialogHeaderLocked: Messages.shareGrantPermissionDialogHeader_lockedProject_virtualConnection,
      permissionsTemplate: Messages.shareGrantPermissionsDialogTemplateDescription_virtualConnection,
      grantAccessMessage: Messages.grantAccessDialogMessage,
    })
    .set(ExplorableItemTypeValue.project, {
      shareOneUser: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_project,
      shareMultipleUsers: Messages.shareGrantPermissionsDialogMessage_project,
      shareOneUserLocked: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_lockedProject_project,
      shareMultipleUsersLocked: Messages.shareGrantPermissionsDialogMessage_lockedProject_project,
      dialogTitle: Messages.shareGrantPermissionsDialogTitle_project,
      dialogHeader: Messages.shareGrantPermissionDialogHeader_project,
      dialogHeaderLocked: Messages.shareGrantPermissionDialogHeader_lockedProject_project,
      permissionsTemplate: Messages.shareGrantPermissionsDialogTemplateDescription_project,
      grantAccessMessage: Messages.grantAccessDialogMessage,
      // AI Access variant: a project aiAccess grant fans out to all workbooks in the project. The
      // header (including the locked-project variant), request message, template description (with its
      // inline "Learn more" link), and bulleted detail all use AI copy. Only aiDialogTitle is
      // intentionally left unset, so the dialog title falls back to the read-grant
      // `shareGrantPermissionsDialogTitle_project`.
      aiDialogHeader: Messages.shareGrantAiAccessDialogHeader_project,
      // A project's permissions can only be controlled by a parent project (never a workbook), so only
      // the locked-project header variant applies -- mirroring the read-grant `dialogHeaderLocked` above.
      aiDialogHeaderLocked: Messages.shareGrantAiAccessDialogHeader_lockedProject_project,
      // Project-specific request copy: unlike the view/workbook variants (which say "to workbook …"),
      // a project grant fans out to every workbook, so it reads "to all workbooks in project …".
      grantAiAccessMessage: Messages.grantAiAccessDialogMessage_project,
      // Shared with the view/workbook mappings above (see note there) — one content-type-agnostic AI template string.
      aiPermissionsTemplate: Messages.shareGrantAiAccessDialogTemplateDescription,
      grantAiAccessMessageDetail: Messages.grantAiAccessDialogMessageDetail,
    })
    .set(ExplorableItemTypeValue.view, {
      shareOneUser: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_view,
      shareMultipleUsers: Messages.shareGrantPermissionsDialogMessage_view,
      shareOneUserLocked: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_lockedProject_view,
      shareOneUserLockedWorkbook: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_lockedWorkbook_view,
      shareMultipleUsersLocked: Messages.shareGrantPermissionsDialogMessage_lockedProject_view,
      shareMultipleUsersLockedWorkbook: Messages.shareGrantPermissionsDialogMessage_lockedWorkbook_view,
      dialogTitle: Messages.shareGrantPermissionsDialogTitle_view,
      dialogHeader: Messages.shareGrantPermissionDialogHeader_view,
      dialogHeaderLocked: Messages.shareGrantPermissionDialogHeader_lockedProject_view,
      dialogHeaderLockedWorkbook: Messages.shareGrantPermissionDialogHeader_lockedWorkbook_view,
      permissionsTemplate: Messages.shareGrantPermissionsDialogTemplateDescription_view,
      grantAccessMessage: Messages.grantAccessDialogMessage,
      aiDialogTitle: Messages.shareGrantAiAccessDialogTitle_view,
      // View-specific request copy: the shared grantAiAccessDialogMessage says "to workbook …", so a
      // view grant uses its own "to view …" variant.
      grantAiAccessMessage: Messages.grantAiAccessDialogMessage_view,
      aiDialogHeader: Messages.shareGrantAiAccessDialogHeader_view,
      aiDialogHeaderLocked: Messages.shareGrantAiAccessDialogHeader_lockedProject_view,
      aiDialogHeaderLockedWorkbook: Messages.shareGrantAiAccessDialogHeader_lockedWorkbook_view,
      // Intentionally shared across view/workbook (no _view/_workbook suffix): the AI Access template
      // description is content-type-agnostic, unlike the read-grant permissionsTemplate above.
      aiPermissionsTemplate: Messages.shareGrantAiAccessDialogTemplateDescription,
      grantAiAccessMessageDetail: Messages.grantAiAccessDialogMessageDetail,
    })
    .set(ExplorableItemTypeValue.workbook, {
      shareOneUser: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_workbook,
      shareMultipleUsers: Messages.shareGrantPermissionsDialogMessage_workbook,
      shareOneUserLocked: Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_lockedProject_workbook,
      shareMultipleUsersLocked: Messages.shareGrantPermissionsDialogMessage_lockedProject_workbook,
      dialogTitle: Messages.shareGrantPermissionsDialogTitle_workbook,
      dialogHeader: Messages.shareGrantPermissionDialogHeader_workbook,
      dialogHeaderLocked: Messages.shareGrantPermissionDialogHeader_lockedProject_workbook,
      permissionsTemplate: Messages.shareGrantPermissionsDialogTemplateDescription_workbook,
      grantAccessMessage: Messages.grantAccessDialogMessage,
      aiDialogTitle: Messages.shareGrantAiAccessDialogTitle_workbook,
      grantAiAccessMessage: Messages.grantAiAccessDialogMessage,
      aiDialogHeader: Messages.shareGrantAiAccessDialogHeader_workbook,
      aiDialogHeaderLocked: Messages.shareGrantAiAccessDialogHeader_lockedProject_workbook,
      // Shared with the view mapping above (see note there) — one content-type-agnostic AI template string.
      aiPermissionsTemplate: Messages.shareGrantAiAccessDialogTemplateDescription,
      grantAiAccessMessageDetail: Messages.grantAiAccessDialogMessageDetail,
    });

  const ContentTypeToTemplateType = new Map<ExplorableItemType, AuthorizableType>([
    [ExplorableItemTypeValue.datasource, AuthorizableType.PROJECT_DATASOURCE_TEMPLATE],
    [ExplorableItemTypeValue.flow, AuthorizableType.PROJECT_FLOW_TEMPLATE],
    [ExplorableItemTypeValue.virtualConnection, AuthorizableType.PROJECT_PUBLISHED_CONNECTION_TEMPLATE],
    [ExplorableItemTypeValue.project, AuthorizableType.PROJECT],
    [ExplorableItemTypeValue.view, AuthorizableType.PROJECT_WORKBOOK_TEMPLATE],
    [ExplorableItemTypeValue.workbook, AuthorizableType.PROJECT_WORKBOOK_TEMPLATE],
  ]);
  const popToast = (toast: IToastOptions): void => {
    const service = ToasterService.instance;
    service.pop(toast);
  };

  const toastHandler = (message: string, isInfo: boolean): void => {
    popToast({
      text: message,
      type: isInfo ? ToastType.Success : ToastType.Error,
    });
};

  const renderBoldLockedProject: () => JSX.Element = () => (
    <span style={{ fontWeight: 'bold', }}>
      {Messages.shareGrantPermissionsDialogMessageLockedPermissions()}
    </span>
  );

  const renderBoldTabbedWorkbook: () => JSX.Element = () => (
    <span style={{ fontWeight: 'bold', }}>
      {Messages.shareGrantPermissionsDialogMessageTabbedViews()}
    </span>
  );

  // The AI Access message detail is a single "|"-separated string. Each segment becomes a bullet;
  // segments are trimmed and empty ones dropped so a localized string with leading/trailing or
  // doubled delimiters (or incidental whitespace around them) never produces an empty bullet.
  export const parseMessageDetailSegments = (detail: string): string[] =>
    detail.split('|').map((segment) => segment.trim()).filter((segment) => segment.length > 0);

  // 14px top margin spaces the list from the request message above. The explicit 0 bottom (via the
  // '14px 0 0' shorthand) overrides the <ul> user-agent default margin-bottom so only the <br/>
  // rendered below the list drives the gap down to the template description — don't simplify this to
  // marginTop, which would leave that default bottom margin in place.
  const renderGrantAiAccessMessageDetail: (detail: string) => JSX.Element = (detail) => (
    <ul data-tb-test-id={MESSAGE_DETAIL_TESTID} style={{ margin: '14px 0 0', paddingInlineStart: '20px' }}>
      {parseMessageDetailSegments(detail).map((segment, index) => (
        <li key={index}>{segment}</li>
      ))}
    </ul>
  );

  export const Contents = (props: ContentsProps) => {
    if (FeatureFlags.grantPermissionsAfterSharing && props.content.contentType !== 'collection') {
      let grantPermissionHeader;
      let grantPermissionMessage;
      const messagesForContent = ContentTypeToMessagesMap.get(props.content.contentType);
      // aiAccess grants show AI-specific request copy; everything else uses the read-grant message.
      const buildRequestAccessMessage = () =>
        props.isAiAccessGrant && messagesForContent.grantAiAccessMessage
          ? messagesForContent.grantAiAccessMessage({ USER_FRIENDLY_NAME: props.requesterName, USERNAME: props.requesterUsername, CONTENT_NAME: props.content.name })
          : messagesForContent.grantAccessMessage({ USER_FRIENDLY_NAME: props.requesterName, USERNAME: props.requesterUsername, CONTENT_NAME: props.content.name });
      // aiAccess grants also swap the "Grant view access…" header and the View-role template description
      // for AI-Access copy so the whole body matches the AI title. Fall back to the read-grant strings
      // when this isn't an aiAccess grant or the content type has no AI variant.
      const useAiCopy = props.isAiAccessGrant;
      const dialogHeaderMsg = useAiCopy && messagesForContent.aiDialogHeader ? messagesForContent.aiDialogHeader : messagesForContent.dialogHeader;
      const dialogHeaderLockedMsg = useAiCopy && messagesForContent.aiDialogHeaderLocked ? messagesForContent.aiDialogHeaderLocked : messagesForContent.dialogHeaderLocked;
      const dialogHeaderLockedWorkbookMsg = useAiCopy && messagesForContent.aiDialogHeaderLockedWorkbook ? messagesForContent.aiDialogHeaderLockedWorkbook : messagesForContent.dialogHeaderLockedWorkbook;
      const permissionsTemplateMsg = useAiCopy && messagesForContent.aiPermissionsTemplate ? messagesForContent.aiPermissionsTemplate : messagesForContent.permissionsTemplate;
      // AI-only: the bulleted detail sits between the request message and the template description.
      // No read-grant fallback — undefined (and so unrendered) for non-AI grants and content types.
      const messageDetailMsg = useAiCopy && messagesForContent.grantAiAccessMessageDetail ? messagesForContent.grantAiAccessMessageDetail : undefined;
      // AI-only: a "Learn more" link rendered inline right after the AI template description. Gated on
      // the AI template being the one shown so it never trails the read-grant description.
      const showAiAccessLearnMore = !!(useAiCopy && messagesForContent.aiPermissionsTemplate);
      // Only the header, the request-access message (buildRequestAccessMessage), and the template
      // description have AI variants. The read-grant fallback body strings below (shareOneUser* /
      // shareMultipleUsers*) are intentionally left as read copy: the aiAccess grant flow always
      // opens with a single recipient AND isRequestAccessDialogEnabled=true (see
      // RequestAccessAction.handleGrantAccess), so those arms are unreachable when isAiAccessGrant
      // is true. Revisit if a multi-recipient or non-request-access AI entry point is ever added.
      if (props.shareRecipients.length === 1) {
        if (props.sourceAuthorizable === undefined) {
          grantPermissionHeader = dialogHeaderMsg({CONTENT_NAME: props.content.name});
          if(props.isRequestAccessDialogEnabled) {
            grantPermissionMessage = buildRequestAccessMessage();
          } else {
            grantPermissionMessage = messagesForContent.shareOneUser({CONTENT_NAME: props.content.name});
          }
        } else if (props.sourceAuthorizable.type === 'project') {
          grantPermissionHeader = dialogHeaderLockedMsg({PROJECT_NAME: props.sourceAuthorizable.name});
          if(props.isRequestAccessDialogEnabled) {
            grantPermissionMessage = buildRequestAccessMessage();
          } else {
            grantPermissionMessage = interpolateString(messagesForContent.shareOneUserLocked({CONTENT_NAME: props.content.name}), {LOCKED_PERMISSIONS: renderBoldLockedProject});
          }
        } else if (props.sourceAuthorizable.type === 'workbook') {
          grantPermissionHeader = dialogHeaderLockedWorkbookMsg({WORKBOOK_NAME: props.sourceAuthorizable.name});
          if(props.isRequestAccessDialogEnabled) {
            grantPermissionMessage = buildRequestAccessMessage();
          } else {
            grantPermissionMessage = interpolateString(messagesForContent.shareOneUserLockedWorkbook({CONTENT_NAME: props.content.name}), {TABBED_VIEWS: renderBoldTabbedWorkbook});
          }
        }
      } else {
        if (props.sourceAuthorizable === undefined) {
          grantPermissionHeader = dialogHeaderMsg({CONTENT_NAME: props.content.name});
          grantPermissionMessage = messagesForContent.shareMultipleUsers({
            CONTENT_NAME: props.content.name,
            TOTAL_USER_COUNT: props.shareRecipients.length,
            TOTAL_USER_COUNT_LOC: Formatters.formatCount(props.shareRecipients.length),
            UNPERMISSIONED_USER_COUNT: props.unpermissionedRecipients.length,
            UNPERMISSIONED_USER_COUNT_LOC: Formatters.formatCount(props.unpermissionedRecipients.length),
          });
        } else if (props.sourceAuthorizable.type === 'project') {
          grantPermissionHeader = dialogHeaderLockedMsg({PROJECT_NAME: props.sourceAuthorizable.name});
          grantPermissionMessage = interpolateString(messagesForContent.shareMultipleUsersLocked({
            CONTENT_NAME: props.content.name,
            TOTAL_USER_COUNT: props.shareRecipients.length,
            TOTAL_USER_COUNT_LOC: Formatters.formatCount(props.shareRecipients.length),
            UNPERMISSIONED_USER_COUNT: props.unpermissionedRecipients.length,
            UNPERMISSIONED_USER_COUNT_LOC: Formatters.formatCount(props.unpermissionedRecipients.length),
          }), {LOCKED_PERMISSIONS: renderBoldLockedProject});
        } else if (props.sourceAuthorizable.type === 'workbook') {
          grantPermissionHeader = dialogHeaderLockedWorkbookMsg({WORKBOOK_NAME: props.sourceAuthorizable.name});
          grantPermissionMessage = interpolateString(messagesForContent.shareMultipleUsersLockedWorkbook({
            CONTENT_NAME: props.content.name,
            TOTAL_USER_COUNT: props.shareRecipients.length,
            TOTAL_USER_COUNT_LOC: Formatters.formatCount(props.shareRecipients.length),
            UNPERMISSIONED_USER_COUNT: props.unpermissionedRecipients.length,
            UNPERMISSIONED_USER_COUNT_LOC: Formatters.formatCount(props.unpermissionedRecipients.length),
          }), {TABBED_VIEWS: renderBoldTabbedWorkbook});
        }
      }
      return (
        <div style={{ fontWeight: 'normal', fontSize: '12px', lineHeight: '18px', color: applyColor(DesignTokens.color.font.default.secondary, '#4F4F4F'), letterSpacing: '0.2px' }}>
          <h3 style={{ fontWeight: 'normal', color:applyColor(DesignTokens.color.font.default.default,'#000000'), marginTop: '0'}}>{grantPermissionHeader}</h3>
          {grantPermissionMessage}
          {messageDetailMsg && renderGrantAiAccessMessageDetail(messageDetailMsg())}
          {
            <div style={{marginBottom: 14}}>
              <br/>
              {permissionsTemplateMsg()}
              {showAiAccessLearnMore && (
                <>
                  {' '}
                  <TextLinkWidget href={HelpService.buildUrl('gpt-permissions-request')} target='_blank' testId={LEARN_MORE_LINK_TESTID}>
                    {Messages.learnMore()}
                  </TextLinkWidget>
                </>
              )}
            </div>
          }
        </div>
      );
    }
    return (
      <div style={{ fontWeight: 'normal', fontSize: '12px', lineHeight: '18px', color: applyColor(DesignTokens.color.font.default.secondary, '#4F4F4F'), letterSpacing: '0.2px' }}>
        {props.shareRecipients.length === 1 &&
          Messages.shareGrantPermissionsDialogSharedWithOneUserMessage_collection({
            CONTENT_NAME: props.content.name
          })
        }
        {props.shareRecipients.length !== 1 &&
          Messages.shareGrantPermissionsDialogMessage_collection({
            CONTENT_NAME: props.content.name,
            TOTAL_USER_COUNT: props.shareRecipients.length,
            TOTAL_USER_COUNT_LOC: Formatters.formatCount(props.shareRecipients.length),
            UNPERMISSIONED_USER_COUNT: props.unpermissionedRecipients.length,
            UNPERMISSIONED_USER_COUNT_LOC: Formatters.formatCount(props.unpermissionedRecipients.length)
          })}
      </div>
    );
  };

  interface FooterProps {
    onManagePermissions: () => void;
    onDismiss: () => void;
    onGrantAccess: () => void;
  }
  export const Footer = (props: FooterProps) => {
    return (
      <div data-tb-test-id={`${DIALOG_TESTID}-footer`} className={CollectionsDialogStyles.GrantPermissionsDialogFooter}>
        <span className={CollectionsDialogStyles.GrantPermissionsManagePermissionsButton}>
          <ButtonWidget
            handleClick={props.onManagePermissions}
            buttonType={ButtonType.LowEmphasis}
            children={Messages.shareGrantPermissionsDialogManagePermissionsAction()}
            testId={MANAGE_PERMISSIONS_BUTTON_TESTID}
          />
        </span>
        <span className={CollectionsDialogStyles.GrantPermissionsRightButtons}>
          <div style={{paddingLeft: TabStyles.Spacing.MediumSpace}}>
            <ButtonWidget
              handleClick={props.onDismiss}
              buttonType={ButtonType.Outline}
              children={Messages.shareGrantPermissionsDialogDismiss()}
              testId={DISMISS_BUTTON_TESTID}
            />
          </div>
          <div style={{paddingLeft: TabStyles.Spacing.MediumSpace}}>
            <ButtonWidget
              handleClick={props.onGrantAccess}
              buttonType={ButtonType.Primary}
              children={Messages.shareGrantPermissionsDialogGrantAccess()}
              testId={GRANT_ACCESS_BUTTON_TESTID}
            />
          </div>
        </span>
      </div>);
  };

  export function Options(
    footerProps: FooterProps,
    contentProps: ContentsProps): ReactModalOptions {
      const titleMessages = ContentTypeToMessagesMap.get(contentProps.content.contentType);
      const dialogTitle = contentProps.isAiAccessGrant && titleMessages.aiDialogTitle
        ? titleMessages.aiDialogTitle()
        : titleMessages.dialogTitle();
      return {
        title: dialogTitle,
        testId: DIALOG_TESTID,
        type: DialogType.NormalWithTitleBar,
        showCloseButton: true,
        positioningBehavior: DialogPositioningBehavior.Centered,
        dialogWidth: DIALOG_WIDTH,
        dialogBodyStyle: { maxWidth: DIALOG_WIDTH + 'px' },
        dialogContent: <Contents {...contentProps} />,
        dialogContentStyle: { padding: 0 },
        footerContent: <Footer {...footerProps} />
      };
  }

  export function open(props: ContentsProps) {
    let modalInstance: ModalInstance<DialogResult>;

    let authorizable: ITypedId = { type: props.content.contentType, id: props.content.id };
    if (props.sourceAuthorizable !== undefined) {
      if (props.sourceAuthorizable.type === 'project') {
        authorizable = {type: ContentTypeToTemplateType.get(props.content.contentType), id: props.sourceAuthorizable.id };
      } else if (props.sourceAuthorizable.type === 'workbook') {
        authorizable = {type: props.sourceAuthorizable.type, id: props.sourceAuthorizable.id };
      }
    }
    // An aiAccess grant on a project targets every workbook in the project through the project's
    // Workbooks permission template: `connect`/`ai_access` are workbook capabilities and don't exist
    // on the bare `project` authorizable, so writing to {type: 'project'} would silently drop both
    // and the recipient would still have no AI access. Repoint to projectWorkbookTemplate, keyed by
    // the same id (the project itself when unlocked, or the controlling parent project when locked).
    // This mirrors the aiAccess gap-check, which reads that same template bucket
    // (GrantAccessPermissionsHelper.getUsersWithoutAiAccessPermissionsForContent) -- grant and
    // gap-check must agree or the dialog would reopen forever. Workbook/view carry the capabilities
    // directly, and when locked they already repoint to the container above, so only `project` needs this.
    if (props.isAiAccessGrant && props.content.contentType === ExplorableItemTypeValue.project) {
      authorizable = { type: AuthorizableType.PROJECT_WORKBOOK_TEMPLATE, id: authorizable.id };
    }
    const capabilitySections = CapabilitySectionsByAuthorizableType(
      getSessionManagementServiceInstance().editingFlowsEnabled(),
      FeatureFlags.explainDataPermissionEnabled,
      getSessionManagementServiceInstance().databasesInProjectsPostMVPEnabled(),
      FeatureFlags.allowNonAdminRefreshExtractsEnabled
    );
    const permissionRoleHelper = new PermissionRoleHelper(capabilitySections);
    const viewTemplate = PermissionsHelper.capabilityValuesFromPermissions(PermissionsHelper.IPermissionsFromPermissions(permissionRoleHelper.getPermissionsForTypeAndRole(authorizable.type, PermissionRoleName.View)));
    // aiAccess grants set both `connect` ("Full Data Query", surfaced as connectWorkbook) and
    // `ai_access`: AI features query the workbook's data, so `ai_access` alone is not enough. The
    // requester is assumed to already have read. The View role template contains neither capability,
    // so derive a minimal template from AI_ACCESS_CAPABILITY_SCOPE — the single source of truth that
    // also drives the aiAccess gap-check, so the two cannot drift.
    const grantTemplate: ICapabilityValue[] = props.isAiAccessGrant
      ? AI_ACCESS_CAPABILITY_SCOPE.map((capability) => ({ capability, value: CapabilityValue.Allowed }))
      : viewTemplate;

    const onManagePermissions = (): void => {
      sendManagePermissionsClickedTelemetry(props.content.contentType);
      const contentAsSet = Set.singletonForIdItem(props.content);
      EditPermissionsAction.showEditPermissionsDialog(contentAsSet, props.content.contentType);
      modalInstance && modalInstance.close(DialogResult.ManagePermissions);
    };

    const onDismiss = (): void => {
      modalInstance && modalInstance.close(DialogResult.Dismiss);
    };

    const onGrantAccess = () => {
      sendGrantAccessDialogClickedTelemetry(props.content.contentType);
      const onSuccess = () => {
        ToasterService.instance.pop({
          type: ToastType.Success,
          text: Messages.shareGrantPermissionsDialogGrantSuccessMsg({
            USER_COUNT: props.unpermissionedRecipients.length,
            USER_COUNT_LOC: Formatters.formatCount(props.unpermissionedRecipients.length)
          })
        });

        // Due to slowness in BE updates we optimistically update collection visibility label in redux
        // after we've successfully updated permissions.
        // Reminder private just means there are no explicit permissions set.
        if (props.content.contentType === 'collection') {
          getAppStore().dispatch(setCollectionVisibility([props.content.id], CollectionVisibility.public));
        }
      };

      const showErrorToast = () => ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.shareGrantPermissionsDialogGrantErrorMsg({
          USER_COUNT: props.unpermissionedRecipients.length,
          USER_COUNT_LOC: Formatters.formatCount(props.unpermissionedRecipients.length)
        })
      });

      setExplicitPermissionsForGrantees(authorizable, props.unpermissionedRecipients, grantTemplate)
        .then(rejectIfResultContainsError)
        .then(onSuccess, showErrorToast)
        .finally(() => modalInstance?.close(DialogResult.GrantPermissions));
    };

    modalInstance = ReactModalService.open(Options(
      { onManagePermissions, onDismiss, onGrantAccess },
      props,
    ));
    return modalInstance.result;
  }
}
